/**
 * @fileoverview The caption starter on the Share screen (owner, 2026-10-12:
 * "something factual that they can build on from there… a few good emojis
 * and hashtag suggestions, but not a fully written caption"): a few plain
 * lines from what Grow knows, filled into the caption box for the gardener
 * to edit, never written by AI.
 *
 *   Parris Island Cos Lettuce
 *   🥬🍂
 *   #lettuce #lettucegarden #growinglettuce #fallgarden #growyourown
 *
 * The plant's one name as the backend says it (lib/plantName,
 * services/grow/plantNaming: "Parris Island Cos Lettuce", "Romaine
 * Lettuce" for a "Not sure" plant); an emoji for the crop and the
 * season's; hashtags from the crop, the season and the general ones, at
 * most sharing.CAPTION_TAGS_MAX. The season is the server's, the planner's
 * season in the garden's climate as Today names it (useGardenSeason;
 * architecture audit A13), never the phone's month: none where the planner
 * has none, and then no season emoji or hashtag. The season's hashtag is in
 * the gardener's language (plants.share.starter.seasonTag). The crop's emoji and
 * hashtags are its group's (plant_groups.share_emoji and share_tags, sent
 * on plant.plant); a crop with none gets the seedling and its own name as
 * a hashtag. The season's emoji, the general hashtags and the most hashtags
 * are Grow settings (Sharing tab: sharing.CAPTION_SEASON_EMOJI, CAPTION_TAGS,
 * CAPTION_TAGS_MAX). A bed's starter is its name and how many plants grow
 * there, with the seedling, the season and the general hashtags. The
 * garden's town and zone are left out: they say roughly where the gardener
 * lives. Days since planting and the bed were left out too (owner,
 * 2026-10-12: "simplify… dont put days or bed and its confusing").
 *
 * Where: SharePlantDialog's caption (PlantCard, LocationCard and
 * ShareMoments hand it the starter).
 * API: none.
 * Words (en/es): plants.share.starter.plants, plantsOne, seasonTag.spring, summer, fall, winter.
 *
 * @module captionStarter
 */

import type { Plant } from '@/shared/types/plant';
import { growSetting } from '@/shared/lib/growSettings';
import { plantName } from '@/shared/lib/plantName';
import type { FourSeason } from '@/shared/api/today';

/** The emoji for a crop with none of its own, and for a bed. */
const SEEDLING = '🌱';

/** The translator the starter's words come from. */
type Say = (key: string, options?: Record<string, string | number>) => string;

/** The season the starter names: the server's (useGardenSeason), or none. */
type Season = FourSeason | null;

/**
 * Words as one hashtag: "Romaine Lettuce" as "#romainelettuce".
 *
 * @param words - The words.
 * @returns The hashtag, or '' when nothing is left.
 */
export function hashtag(words: string): string {
  const tag = words.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
  return tag ? `#${tag}` : '';
}

/**
 * The starter's last two lines: the emojis and the hashtags.
 *
 * @param emoji - The subject's own emoji.
 * @param tagWords - The subject's own hashtag words (without #), most particular first.
 * @param season - The garden's season now (the server's), or none.
 * @param t - The translator, for the season's hashtag in the gardener's language.
 */
function tail(emoji: string, tagWords: string[], season: Season, t: Say): string[] {
  const seasonTag = season ? hashtag(t(`plants.share.starter.seasonTag.${season}`)) : '';
  const tags = [...new Set([...tagWords.map(hashtag), seasonTag, ...(growSetting('sharing.CAPTION_TAGS') ?? []).map(hashtag)].filter(Boolean))]
    .slice(0, growSetting('sharing.CAPTION_TAGS_MAX') ?? 0);
  const seasonEmoji = season ? growSetting('sharing.CAPTION_SEASON_EMOJI')?.[season]?.emoji ?? '' : '';
  return [`${emoji}${seasonEmoji}`, tags.join(' ')];
}

/**
 * A plant's caption starter.
 *
 * @param plant - The plant.
 * @param season - The garden's season now (useGardenSeason), or none.
 * @param t - The translator.
 * @returns The starter, a line each.
 */
export function plantCaptionStarter(plant: Plant, season: Season, t: Say): string {
  // The plant's one name (lib/plantName, services/grow/plantNaming): "Devil's Ear Lettuce"; nothing composed here.
  const name = plantName(plant).trim();
  const cropWord = plant.names.cropName.trim();
  const emoji = plant.plant?.share_emoji?.trim() || SEEDLING;
  const cropTags = (plant.plant?.share_tags ?? '').split(/\s+/).filter(Boolean);
  return [
    name,
    ...tail(emoji, cropTags.length > 0 ? cropTags : [cropWord || name], season, t),
  ].filter(Boolean).join('\n');
}

/**
 * A bed's caption starter: its name and how many plants grow there.
 *
 * @param name - The bed's name.
 * @param plantCount - How many plants grow there.
 * @param t - The translator.
 * @param season - The garden's season now (useGardenSeason), or none.
 * @returns The starter, a line each.
 */
export function bedCaptionStarter(name: string, plantCount: number, t: Say, season: Season): string {
  return [
    name.trim(),
    plantCount > 0 ? t(plantCount === 1 ? 'plants.share.starter.plantsOne' : 'plants.share.starter.plants', { count: plantCount }) : null,
    ...tail(SEEDLING, [], season, t),
  ].filter(Boolean).join('\n');
}
